import canonicalData from "./trust-badges-canonical.json";

/**
 * Canonical trust-badge vocabulary — single source of truth shared by the
 * storefront (label table, parser) and the Saleor maintenance scripts
 * (scripts/canonicalize-trust-badges.mjs, scripts/check-trust-badges.mjs),
 * which read the same JSON via readFileSync.
 *
 * `canonical` is ordered by display priority: product cards render only the
 * first two badges, so the order here decides what customers actually see.
 * `aliases` maps every legacy Saleor variant to its slug; `null` retires a
 * variant entirely (shipping claims, stack marketing — deliberately no
 * cold-chain and no "CoA enclosed" style claims anywhere in this vocabulary).
 */
export const CANONICAL_TRUST_BADGE_SLUGS: readonly string[] = canonicalData.canonical;

const ALIASES: Readonly<Record<string, string | null>> = canonicalData.aliases;

const PRIORITY = new Map(CANONICAL_TRUST_BADGE_SLUGS.map((slug, index) => [slug, index]));

export function isCanonicalTrustBadge(value: string): boolean {
	return PRIORITY.has(value);
}

/** Canonical slug for a legacy variant; null = retired; undefined = unknown. */
export function resolveTrustBadgeAlias(value: string): string | null | undefined {
	return ALIASES[value];
}

/**
 * Normalizes a raw ux_trust_badges_json array: maps legacy variants to
 * canonical slugs, drops retired variants, dedupes (a product may carry two
 * variants of the same claim) and sorts by display priority. Unknown values
 * pass through visibly (rendered raw + dev warning downstream) so vocabulary
 * drift in Saleor stays detectable instead of silently disappearing.
 */
export function canonicalizeTrustBadges(raw: readonly string[]): string[] {
	const seen = new Set<string>();
	const badges: string[] = [];
	for (const value of raw) {
		const alias = isCanonicalTrustBadge(value) ? value : ALIASES[value];
		if (alias === null) continue;
		const badge = alias ?? value;
		if (seen.has(badge)) continue;
		seen.add(badge);
		badges.push(badge);
	}
	return badges.sort(
		(a, b) =>
			(PRIORITY.get(a) ?? CANONICAL_TRUST_BADGE_SLUGS.length) -
			(PRIORITY.get(b) ?? CANONICAL_TRUST_BADGE_SLUGS.length),
	);
}
